<template>
  <div class="account-settings-container">
    <div class="settings-content">
      <!-- Page Header -->
      <div class="settings-header">
        <div class="header-left">
          <h1 class="page-title">Account Settings</h1>
          <p class="page-subtitle">Manage your profile information and security settings.</p>
        </div>
      </div>

      <!-- Main Settings Grid -->
      <div class="settings-grid">
        <!-- Profile Section -->
        <div class="settings-card profile-card">
          <o-form @submit.prevent="submit()" autocomplete="off">
            <h3 class="section-title">Personal Information</h3>
            
            <!-- Avatar Section -->
            <div class="avatar-manager">
              <div class="avatar-preview-wrapper">
                <img :src="(!imageErrorStatus && image.url) ? image.url : defaultAvatar" alt="Avatar" class="current-avatar" @error="imageError">
              </div>
              <div class="avatar-controls">
                <label class="control-label">Profile Picture</label>
                <div class="avatar-options">
                  <div class="default-presets">
                    <img 
                      v-for="(avatar, index) in defaultAvatars" 
                      :key="index" 
                      :src="avatar" 
                      @click="selectDefaultAvatar(avatar)" 
                      :class="{ 'selected': selectedAvatar === avatar }" 
                      class="preset-item"
                    >
                    <div class="preset-divider"></div>
                    <div class="upload-trigger">
                      <input type="file" ref="fileInput" @change="handleFileUpload" accept="image/*" class="hidden-input">
                      <button type="button" class="btn-upload-avatar" @click="$refs.fileInput.click()" title="Upload Photo">
                        <i class="fas fa-plus"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Form Content -->
            <div class="form-grid">
              <!-- Name Row -->
              <div class="form-row multi">
                <div class="form-item mini">
                  <label>Title</label>
                  <select v-model="user.title" class="custom-select">
                    <option v-for="t in titles" :key="t.value" :value="t.value">{{ t.text }}</option>
                  </select>
                </div>
                <div class="form-item main">
                  <label>First Name <span class="required">*</span></label>
                  <input type="text" v-model="user.first_name" required class="custom-input" placeholder="Enter first name">
                </div>
                <div class="form-item main">
                  <label>Last Name <span class="required">*</span></label>
                  <input type="text" v-model="user.last_name" required class="custom-input" placeholder="Enter last name">
                </div>
              </div>

              <!-- Contact Row -->
              <div class="form-row duo">
                <div class="form-item">
                  <label>Phone Number <span class="required">*</span></label>
                  <div class="phone-input-group">
                    <span v-if="!unknownCountry" class="country-prefix">{{ countryCode }}</span>
                    <input type="text" v-model="user.phone" required class="custom-input phone-field" placeholder="Phone number" @input="acceptNumber">
                  </div>
                </div>
                <div class="form-item">
                  <label>Email Address <span class="required">*</span></label>
                  <input type="email" v-model="user.email" required class="custom-input" placeholder="Email address">
                </div>
              </div>

              <!-- Location Row 1 -->
              <div class="form-row duo">
                <div class="form-item">
                  <label>Country <span class="required">*</span></label>
                  <select v-model="user.country" required class="custom-select">
                    <option v-for="c in countries" :key="c.value" :value="c.value">{{ c.text }}</option>
                  </select>
                </div>
                <div class="form-item">
                  <label>State/Province <span class="required">*</span></label>
                  <input type="text" v-model="user.state" required class="custom-input" placeholder="State/Province">
                </div>
              </div>

              <!-- Location Row 2 -->
              <div class="form-row duo">
                <div class="form-item">
                  <label>City/Area <span class="required">*</span></label>
                  <input type="text" v-model="user.city" required class="custom-input" placeholder="City">
                </div>
                <div class="form-item">
                  <label>Post Code <span class="required">*</span></label>
                  <input type="text" v-model="user.postcode" required class="custom-input" placeholder="Post Code">
                </div>
              </div>

              <!-- Address -->
              <div class="form-item full">
                <label>Address Information</label>
                <textarea v-model="user.address" class="custom-textarea" placeholder="Enter your full address" rows="3"></textarea>
              </div>
            </div>

            <!-- Actions -->
            <div class="form-actions">
              <button type="button" class="btn-cancel-clean" @click="cancel">Cancel</button>
              <button type="submit" class="btn-save-clean" :disabled="page.isLoading">
                <span v-if="page.isLoading"><i class="fas fa-spinner fa-spin me-2"></i> Saving...</span>
                <span v-else>Save Changes</span>
              </button>
            </div>
          </o-form>
        </div>

        <!-- Security Section -->
        <div class="settings-card security-card">
          <h3 class="section-title">Security & Protection</h3>
          <p class="section-desc">Keep your account secure with advanced authentication features.</p>
          
          <TwoFactorToggle />
        </div>
      </div>
    </div>

    <!-- Global Loading Overlay -->
    <div v-if="page.isLoading && !user.id" class="global-loader">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script>
import { OForm } from '@/components/OmnihControl';
import TwoFactorToggle from '@/components/two-factor/TwoFactorToggle.vue';
import { computed, onMounted, reactive, watch, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router';

/**
 * Account settings view for managing user profile and security.
 * Implements a modern card-based layout with integrated 2FA management.
 */
export default {
    name: 'AccountSettings',
    components: {
        OForm,
        TwoFactorToggle
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.previousRouteName = from.name;
        });
    },
    setup() {
        const store = useStore();
        const router = useRouter();

        const page = reactive({
            isLoading: false,
            cardTitle: 'Edit User'
        });

        const user = reactive({
            title: '',
            first_name: '',
            last_name: '',
            id: "",
            corporate_id: "",
            email: "",
            avatar: "",
            phone: "",
            address: "",
            state: "",
            country: 0,
            city: "",
            postcode: "",
        });

        const image = reactive({ url: "" });
        const imageErrorStatus = ref(false);
        function imageError() {
            imageErrorStatus.value = true;
        }

        watch(() => image.url, () => {
            imageErrorStatus.value = false;
        });

        const displayMemberName = computed(() => {
            const u = currUser.value;
            const firstName = user.first_name || u?.first_name || '';
            const lastName = user.last_name || u?.last_name || '';
            if (firstName || lastName) {
                return `${firstName} ${lastName}`.trim();
            }
            return u?.name || u?.email || '-';
        });

        const defaultAvatar = computed(() => {
            const title = user.title || currUser.value?.title || '';
            return title === 'Mr.'
                ? 'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/man.png'
                : 'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/woman.png';
        });

        const getInitials = computed(() => {
            const name = displayMemberName.value;
            if (!name || name === '-') return '?';
            const words = name.split(' ').filter(Boolean);
            if (words.length >= 2) {
                return (words[0][0] + words[1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        });

        const titles = ref([
            { value: 'Mr.', text: 'Mr.' },
            { value: 'Mrs.', text: 'Mrs.' },
            { value: 'Miss', text: 'Miss' }
        ]);

        const currUser = computed(() => store.state.Auth.currUser);
        const previousRouteName = ref(null);
        const countries = ref([]);
        const countriesData = ref([]);
        const countryCode = ref('');
        const unknownCountry = ref(false);
        const fileInput = ref(null);

        const defaultAvatars = [
            'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/woman.png',
            'https://s3.ap-southeast-1.amazonaws.com/cdn-member.omnihotelier.com/images/man.png',
        ];

        const selectedAvatar = ref(null);

        watch(() => user.country, updatePhoneCode);
        watch(() => currUser.value.id, () => { loadState(); });

        onMounted(async () => {
            page.isLoading = true;
            await loadCountries();
            await loadState();
            page.isLoading = false;
        });

        /**
         * Fetch available countries for the location selects.
         */
        const loadCountries = async () => {
            try {
                const response = await store.dispatch('Countries/read');
                countriesData.value = response;
                countries.value = response.map(country => ({ value: country.id, text: country.name }));
            } catch (err) {
                console.error('[Account] Failed to load countries:', err);
            }
        }

        /**
         * Update the displayed phone prefix based on selected country.
         */
        function updatePhoneCode() {
            if (!user.country) {
                unknownCountry.value = true;
                return;
            }

            const country = countriesData.value.find(c => c.id == user.country);

            if (country && country.phone_code) {
                unknownCountry.value = false;
                countryCode.value = country.phone_code;
            } else {
                unknownCountry.value = true;
            }
        }

        /**
         * Load user profile data from the store.
         */
        const loadState = async () => {
            const id = currUser.value.id;
            if (!id) return;

            try {
                const data = await store.dispatch('User/read_single', id);
                user.country = data.user_profile.country;
                user.id = data.id;
                user.title = data.title;
                user.first_name = data.first_name;
                user.last_name = data.last_name;
                user.email = data.email;
                user.phone = removeCountryCode(data.user_profile.phone);
                user.address = data.user_profile.address;
                user.state = data.user_profile.state;
                user.city = data.user_profile.city;
                user.postcode = data.user_profile.postcode;

                image.url = data.user_profile.avatar?.image_url || '';
            } catch (err) {
                console.error('[Account] Failed to load user state:', err);
            }
        }

        /**
         * Strip country code from phone number for display in the input.
         * 
         * @param {string} phone - The full phone number string
         * @returns {string} Phone number without leading country code
         */
        const removeCountryCode = (phone) => {
            if (!user.country || !phone) return phone;

            const country = countriesData.value.find(c => c.id == user.country);
            if (country && country.phone_code) {
                const escapedPhoneCode = country.phone_code.replace('+', '\\+');
                const phoneCodePattern = new RegExp(`^${escapedPhoneCode}`);
                return phone.replace(phoneCodePattern, '').trim();
            }
            return phone;
        }

        /**
         * Enforce numeric input for phone number.
         */
        function acceptNumber(event) {
            const input = event.target;
            let val = input.value.replace(/[^0-9]/g, '');
            if (val.startsWith('0')) {
                val = val.replace(/^0+/, '');
            }
            input.value = val;
            user.phone = val;
        }

        /**
         * Handle manual file upload.
         */
        function handleFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    user.avatar = e.target.result;
                    image.url = e.target.result;
                    selectedAvatar.value = e.target.result;
                };
                reader.readAsDataURL(file);
            }
        }

        /**
         * Handle selection of a default avatar preset.
         */
        function selectDefaultAvatar(img) {
            user.avatar = img;
            image.url = img;
            selectedAvatar.value = img;
        }

        /**
         * Submit the profile update form.
         */
        function submit() {
            const data = { ...user };
            
            // Clean phone number (prevent double prefixing)
            let rawPhone = data.phone.trim().replace(/^0+/, ''); // Remove leading zeros
            data.phone = countryCode.value + rawPhone;
            
            page.isLoading = true;

            if (data.avatar === '') {
                delete data.avatar;
            }

            store.dispatch('User/update', { data })
                .then(() => {
                    // Update current user in auth store
                    store.dispatch('Auth/auth_check');
                    router.push({ name: previousRouteName.value ?? 'Home' });
                })
                .catch((err) => {
                    console.error('[Account] Update failed:', err);
                })
                .finally(() => {
                    page.isLoading = false;
                });
        }

        /**
         * Navigate back without saving.
         */
        function cancel() {
            router.push({ name: previousRouteName.value ?? 'Home' });
        }

        return {
            user,
            submit,
            page,
            image,
            defaultAvatar,
            imageErrorStatus,
            imageError,
            getInitials,
            currUser,
            titles,
            previousRouteName,
            cancel,
            countries,
            countryCode,
            unknownCountry,
            fileInput,
            defaultAvatars,
            selectedAvatar,
            selectDefaultAvatar,
            handleFileUpload,
            acceptNumber
        }
    }
}
</script>

<style scoped>
.account-settings-container {
    width: 100%;
    min-height: calc(100vh - 64px);
    background-color: #f8fafc;
    padding: 32px 24px;
}

.settings-content {
    max-width: 1000px;
    margin: 0 auto;
}

.settings-header {
    margin-bottom: 32px;
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 8px;
}

.page-subtitle {
    color: #64748b;
    font-size: 15px;
    margin: 0;
}

.settings-grid {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.settings-card {
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03);
    padding: 32px;
    border: 1px solid #f1f5f9;
}

.section-title {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 24px;
}

.section-desc {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 24px;
}

/* Avatar Section Styles */
.avatar-manager {
    display: flex;
    gap: 28px;
    align-items: center;
    margin-bottom: 32px;
    padding: 20px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 16px;
}

.avatar-preview-wrapper {
    position: relative;
    width: 88px;
    height: 88px;
    border-radius: 50%;
    overflow: hidden;
    border: 4px solid white;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.current-avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.avatar-preview-wrapper .member-avatar-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--primary-gradient, linear-gradient(135deg, #F93E3E 0%, #E02020 100%));
    color: #ffffff;
    font-size: 30px;
    font-weight: 600;
    user-select: none;
}

.avatar-controls {
    flex: 1;
}

.control-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.avatar-options {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.default-presets {
    display: flex;
    gap: 12px;
}

.preset-item {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    object-fit: cover;
}

.preset-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.preset-item.selected {
    border-color: var(--primary-color, #fc6666);
    transform: scale(1.05);
}

.preset-divider {
    width: 1px;
    height: 32px;
    background: #e2e8f0;
    margin: 0 4px;
}

.hidden-input {
    display: none;
}

.btn-upload-avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 2px dashed #cbd5e1;
    background: white;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-upload-avatar:hover {
    border-color: var(--primary-color, #fc6666);
    color: var(--primary-color, #fc6666);
    background: var(--secondary-color, rgba(252, 102, 102, 0.05));
}

/* Form Styles */
.form-grid {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-row {
    display: flex;
    gap: 20px;
}

.form-row.duo > .form-item {
    flex: 1;
}

.form-item {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-item label {
    font-size: 13px;
    font-weight: 500;
    color: #475569;
}

.required {
    color: #ef4444;
}

.custom-input, .custom-select, .custom-textarea {
    width: 100%;
    padding: 10px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    color: #1e293b;
    outline: none;
    transition: all 0.2s;
    background-color: white;
}

.custom-input:focus, .custom-select:focus, .custom-textarea:focus {
    border-color: var(--primary-color, #fc6666);
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(252, 102, 102, 0.1));
}

.form-row.multi .mini {
    flex: 0 0 100px;
}

.form-row.multi .main {
    flex: 1;
}

.phone-input-group {
    display: flex;
    align-items: center;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    overflow: hidden;
    background-color: white;
}

.country-prefix {
    padding: 10px 14px;
    background-color: #f1f5f9;
    border-right: 1px solid #e2e8f0;
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
    min-width: 60px;
    text-align: center;
}

.phone-field {
    border: none !important;
    box-shadow: none !important;
}

.form-actions {
    margin-top: 40px;
    padding-top: 24px;
    border-top: 1px solid #f1f5f9;
    display: flex;
    justify-content: flex-end;
    gap: 12px;
}

.btn-cancel-clean {
    padding: 10px 24px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #64748b;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-cancel-clean:hover {
    background: #f8fafc;
    color: #1e293b;
}

.btn-save-clean {
    padding: 10px 32px;
    background: var(--primary-color, #fc6666);
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-save-clean:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px var(--secondary-color, rgba(252, 102, 102, 0.2));
}

.btn-save-clean:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.global-loader {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255,255,255,0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
}

@media (max-width: 768px) {
    .form-row {
        flex-direction: column;
        gap: 16px;
    }
    .form-row.multi .mini {
        flex: 1;
    }
    .avatar-manager {
        flex-direction: column;
        text-align: center;
    }
    .default-presets {
        justify-content: center;
    }
}
</style>